import React, { useState, lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import { useInView } from 'react-intersection-observer'
// Pre-imported critical components (always needed)
import Navbar from "./components/Navbar"
import PageReveal from "./components/PageReveal"
import SmoothScrollProvider from "./components/Lenis"
import Footer from "./components/Footer"
import ScrollToTop from "./components/ScrollToTop"
import CustomCursor from "./components/CustomCursor"
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

// Lazy loaded route pages (code-splitting)
const Home = lazy(() => import("./components/Home"));
const Works = lazy(() => import("./components/Works"));
const ServiceDetail = lazy(() => import("./components/ServiceDetail"));
const Contact = lazy(() => import("./components/Contact"));
const AboutPage = lazy(() => import("./components/AboutPage"));
const ServicesPage = lazy(() => import("./components/ServicesPage"));
const ThankYou = lazy(() => import("./components/ThankYou"));

// Lazy loaded 3D Scene
const CanvasScene = lazy(() => import("./components/CanvasScene"));

gsap.registerPlugin(ScrollTrigger);

// Lightweight page spinner for Suspense
const PageSpinner = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#050505' }}>
    <div style={{ width: 40, height: 40, border: '3px solid rgba(240,78,35,0.2)', borderTopColor: '#f04e23', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const AppContent = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isServicesPage = location.pathname === '/services';
  const isServiceDetailPage = location.pathname.startsWith('/services/');
  const isAboutPage = location.pathname === '/about';
  const isContactPage = location.pathname === '/contact';
  const isWorkPage = location.pathname === '/works';

  const { ref: sceneRef, inView: sceneInView } = useInView({
    triggerOnce: true,
    rootMargin: '200px 0px', // Start loading slightly before it's actually in view
  });

  return (
    <SmoothScrollProvider>
      <div ref={sceneRef} className='relative min-h-screen'>
        {/* Background Video Layer — always mounted to avoid re-loading on navigation */}
        <div className="fixed inset-0 z-[-1] pointer-events-none">
          <video
            key="global-bg"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            src="/videos/background3.mp4"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>

        {/* Grain/Noise Overlay — CSS-only, no external request */}
        <div className="fixed inset-0 pointer-events-none z-[9999] opacity-[0.02] mix-blend-overlay" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E\")" }} />

        {/* Global 3D Scene - Home Page Only */}
        {isHomePage && sceneInView && (
          <div className="fixed top-0 left-0 w-full h-screen pointer-events-none z-[5]">
            <Suspense fallback={null}>
              <CanvasScene isLoaded={isLoaded} />
            </Suspense>
          </div>
        )}

        {/* Main Content Layer */}
        <div className="relative z-10">

          <CustomCursor />
          <PageReveal onComplete={() => setIsLoaded(true)} />
          <Navbar />

          <Suspense fallback={<PageSpinner />}>
            <Routes>
              <Route path="/" element={<Home isLoaded={isLoaded} />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/works" element={<Works />} />
              <Route path="/services/:id" element={<ServiceDetail />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/thank-you" element={<ThankYou />} />
            </Routes>
          </Suspense>

          <Footer key={location.pathname} />
        </div>
      </div>
    </SmoothScrollProvider>
  );
};

const App = () => {
  return (
    <Router>
      <ScrollToTop />
      <AppContent />
    </Router>
  )
}

export default App

